import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDeleteScenarioApprovals } from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { showToast } from "@/utils/toastUtils";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Scenario } from "@/types/scenario";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/libs/react-query/queryKeys";

type Props = {
  approvalId: string;
  scenario: Scenario;
  children: React.ReactNode;
};

export default function CancelApprovalDialog({
  approvalId,
  scenario,
  children,
}: Props) {
  const { group_id = "" } = useParams();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { mutate: deleteApproval } = useDeleteScenarioApprovals();

  const handleCancel = () => {
    // 승인 요청 취소 (백엔드에서 자동으로 시나리오의 approval_id도 제거됨)
    deleteApproval(
      { approval_id: approvalId },
      {
        onSuccess: () => {
          setTimeout(() => {
            // 승인 관련 쿼리 갱신
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.SCENARIO_APPROVALS.LIST_ROOT,
            });

            // 승인 상세 쿼리 갱신 - 이게 /v1/approvals/{approval_id}/read 호출
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.SCENARIO_APPROVALS.DETAIL(approvalId),
            });

            // 시나리오 목록 갱신
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.SCENARIO.LIST(group_id),
            });

            // 강제 refetch
            queryClient.refetchQueries({
              queryKey: QUERY_KEYS.SCENARIO.LIST(group_id),
            });

            // 승인 상세도 강제 refetch
            queryClient.refetchQueries({
              queryKey: QUERY_KEYS.SCENARIO_APPROVALS.DETAIL(approvalId),
            });
          }, 1000); // 1초 딜레이

          showToast({
            message:
              "승인 요청이 취소되었습니다. 시나리오를 다시 수정할 수 있습니다.",
            type: "success",
          });
          setOpen(false);
        },
        onError: () => {
          showToast({
            message: "승인 요청 취소에 실패했습니다.",
            type: "error",
          });
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>승인 요청 취소</AlertDialogTitle>
          <AlertDialogDescription className="mt-3">
            승인 요청을 취소하시겠습니까?
            <br /> 취소 후에는 시나리오를 다시 수정하고 새로운 승인 요청을 할 수
            있습니다.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>취소</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleCancel}
            className="bg-pink hover:bg-pink/80"
          >
            승인 요청 취소
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
